import { useId } from 'react';
import { Button } from '@carbon/react';
import { Renew } from '@carbon/react/icons';
import { POLLING_OPTIONS } from './utils';
import styles from './auto-refresh-controls.module.scss';

interface AutoRefreshControlsProps {
  /** When the shown stats were read (ISO 8601); null before the first load. */
  generatedAt: string | null;
  onRefresh: () => void;
  /** Disables Refresh while a refresh is in flight. */
  refreshing?: boolean;
  /** Milliseconds between refreshes; 0 is Off. */
  pollingInterval: number;
  onPollingIntervalChange: (interval: number) => void;
}

/**
 * The "Last updated · Refresh" line and the Auto-refresh choice shared by the
 * Sidekiq queue admin pages.
 *
 * @param {AutoRefreshControlsProps} props - What was last read, and the refresh and polling handlers.
 * @return The controls.
 */
export function AutoRefreshControls({
  generatedAt,
  onRefresh,
  refreshing = false,
  pollingInterval,
  onPollingIntervalChange,
}: AutoRefreshControlsProps) {
  const labelId = useId();

  return (
    <>
      <div className={styles.lastUpdated}>
        {generatedAt &&
          `Last updated: ${new Date(generatedAt).toLocaleTimeString()}`}
        <Button
          className={styles.refreshButton}
          kind="ghost"
          size="sm"
          renderIcon={Renew}
          onClick={onRefresh}
          disabled={refreshing}
        >
          Refresh
        </Button>
      </div>
      <div
        className={styles.pollingControls}
        role="group"
        aria-labelledby={labelId}
      >
        <span id={labelId} className={styles.pollingLabel}>
          Auto-refresh:
        </span>
        {POLLING_OPTIONS.map((opt) => (
          <Button
            key={opt.value}
            kind={pollingInterval === opt.value ? 'primary' : 'ghost'}
            aria-pressed={pollingInterval === opt.value}
            size="sm"
            className={styles.pollingButton}
            onClick={() => onPollingIntervalChange(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </div>
    </>
  );
}
